import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form, Field } from 'formik';
import {
    TextField,
    FormControlLabel,
    Radio,
    RadioGroup,
    FormControl,
    FormLabel,
    MenuItem,
    Select,
    Checkbox,
    Box,
    Typography,
    FormGroup,
} from '@mui/material';
import { ToastContainer, toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import ShortUniqueId from 'short-unique-id';
import axios from 'axios';
import { UserDataContext } from 'context';


const renderField = (field) => {
    switch (field.type) {
        case 'text':
            return <Field as={TextField} fullWidth name={field.name} type="text" variant="outlined" />;
        case 'number':
            return <Field as={TextField} fullWidth name={field.name} type="number" variant="outlined" inputProps={{ min: 1 }} />;
        case 'phone':
            return <Field as={TextField} fullWidth name={field.name} type="tel" variant="outlined" placeholder="eg.+94712345678" />;
        case 'email':
            return <Field as={TextField} fullWidth name={field.name} type="email" variant="outlined" placeholder="eg. abc@zmail.com" />;
        case 'image':
            return <Box marginTop={1}><img src={field.formImage.secure_url} alt='image here' width="150" height="200" /></Box>;
        case 'radio':
            return (
                <FormControl component="fieldset">
                    {/* <FormLabel component="legend">{field.name}</FormLabel> */}
                    <RadioGroup row sx={{ marginLeft: 2 }}>
                        {field.options.split(',').map((options) => (
                            <FormControlLabel
                                key={options}
                                control={<Field as={Radio} type="radio" />}
                                label={options}
                                value={options}
                                name={field.name}
                            />
                        ))}
                    </RadioGroup>
                </FormControl>
            );
        case 'select':
            return (
                <Field name={field.name}>
                    {({ field: { value, name }, form: { setFieldValue } }) => (
                        <FormControl fullWidth variant="outlined">
                            <Select
                                value={value}
                                onChange={(e) => setFieldValue(name, e.target.value)}
                                displayEmpty
                                sx={{ height: '45px' }}
                            >
                                <MenuItem value="">
                                    <em>Select an option</em>
                                </MenuItem>
                                {field.options.split(',').map((options) => (
                                    <MenuItem key={options} value={options}>
                                        {options}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                </Field>
            );
        case 'date':
            return <Field as={TextField} fullWidth name={field.name} type="date" variant="outlined" InputLabelProps={{ shrink: true }} />;
        case 'checkbox':
            return (
                <FormGroup>
                    {field.options.split(',').map((option) => (
                        <FormControlLabel
                            key={option}
                            control={
                                <Field
                                    as={Checkbox}
                                    type="checkbox"
                                    name={field.name}
                                    value={option}
                                />
                            }
                            label={option}
                        />
                    ))}
                </FormGroup>
            );
        case 'password':
            return <Field as={TextField} fullWidth name={field.name} type="password" variant="outlined" />;
        default:
            return null;
    }
};

const DynamicForm = ({ fields, eventId, eventName, venueName, date, time, categoryId, categoryName, price, eventOrganizationId, bookedBy, isAllowedToBook, ticketPurchaseLimit, isSingleRegistrationPerTicket, ticketsCount, seats, isSeat }) => {
    const userDetails = useContext(UserDataContext);
    const userId = userDetails && userDetails?.userData?.id;
    const uid = new ShortUniqueId({ dictionary: 'number', length: 6 });
    const navigate = useNavigate();
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const bearerToken = localStorage.getItem('token');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const defaultFields = [
        { name: 'Name', type: 'text' },
        { name: 'Email', type: 'email' },
        { name: 'Phone', type: 'phone' }
    ];
    // merging default fields with dynamic fields and remove duplicate if it is in default
    const mergedFields = [...defaultFields, ...fields.filter(f => !['Name', 'Email', 'Phone'].includes(f.name))];

    const initialValues = mergedFields.reduce((acc, field) => {
        acc[field.name] = '';
        return acc;
    }, {});

    const validate = (values) => {
        const errors = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const phoneRegex = /^\+?[1-9]\d{1,14}$/;

        mergedFields.forEach((field) => {
            const isFieldRequired = defaultFields.includes(field) || field.isRequired;

            if (isFieldRequired && field.type !== 'image') {
                if (!values[field.name]) {
                    errors[field.name] = `${field.name} is required`;
                }
            }
            if (field.type === 'email' && values[field.name]) {
                if (!emailRegex.test(values[field.name])) {
                    errors[field.name] = `${field.name} must be a valid email address`;
                }
            }
            if (field.type === 'phone' && values[field.name]) {
                if (!phoneRegex.test(values[field.name])) {
                    errors[field.name] = `${field.name} must be a valid phone number`;
                }
            }
        });

        return errors;
    };

    const currentDate = new Date().toISOString().split("T")[0];
    const handleSubmit = async (values, { resetForm }) => {
        try {
            setIsSubmitting(true);
            const { Name: name, Email: email, Phone: phone, ...details } = values;

            const dataToAdd = [{
                name,
                email,
                phone,
                details,
                userId,
                eventId,
                orgId: eventOrganizationId,
                tickets: Array.from({ length: ticketsCount }, (_, i) => {
                    const ticket = {
                        ticketCategoryId: categoryId,
                        price: Number(price),
                    };

                    if (isSeat && seats[i]) {
                        ticket.seatId = seats[i].id;
                    }

                    return ticket;
                }),
                bookedBy,
            }];

            const response = await axios.post(
                `${apiUrl}event-service/event-registration/book-tickets`,
                dataToAdd,
                { headers: { 'Content-Type': 'application/json', accept: 'application/json' } }
            );

            if (!response.data.success) throw new Error("Failed to add registrations");

            const data = response?.data?.data?.[0];
            if (!data) throw new Error("Invalid response data");
            const { eventRegistration } = data;
            const registrationId = eventRegistration?.id;
            resetForm();
            document.activeElement.blur();
            toast.success("Tickets successfully booked and entries registered!", {
                onClose: () => {
                    setIsSubmitting(false);
                    navigate(`/eventBookings/book-ticket/ticket-view`, { state: { eventId, registrationId } });
                },
            });

        } catch (error) {
            console.error("Error submitting form:", error);
            toast.error(error?.message || "An error occurred while booking tickets.");
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <Formik initialValues={initialValues} validate={validate} onSubmit={handleSubmit}>
                {({ values, errors, touched }) => (
                    <Form>
                        <div>
                            {mergedFields.map((field) => (
                                <div key={field.name} className="mb-4">
                                    <label className="block text-lg font-semibold mb-2">
                                        {field.name}
                                    </label>
                                    {renderField(field)}
                                    {errors[field.name] && touched[field.name] && (
                                        <p className="mt-1 text-sm text-red-600">
                                            {errors[field.name]}
                                        </p>
                                    )}
                                </div>
                            ))}

                            {!categoryId && (
                                <p className="mt-4 text-sm text-amber-500 font-light">
                                    Select any ticket category to continue...
                                </p>
                            )}
                            {isSeat && seats.length !== ticketsCount && (
                                <p className="text-sm text-amber-500 font-light">
                                    Select seats based on ticket count to continue...
                                </p>
                            )}

                            {categoryId && !isAllowedToBook && (
                                <p className="mt-4 text-sm text-amber-500 font-light">
                                    Sold out
                                </p>
                            )}

                            <button
                                type="submit"
                                disabled={!(categoryId && isAllowedToBook) || isSubmitting || (isSeat && seats.length !== ticketsCount)}
                                className="mt-4 px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Register & Book Ticket
                            </button>
                        </div>
                    </Form>
                )}
            </Formik>

            <ToastContainer
                position="bottom-right"
                autoClose={2000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
        </>
    );
};

export default DynamicForm;

DynamicForm.propTypes = {
    fields: PropTypes.isRequired,
    eventId: PropTypes.isRequired,
    eventName: PropTypes.isRequired,
    date: PropTypes.isRequired,
    time: PropTypes.isRequired,
    categoryId: PropTypes.isRequired,
    categoryName: PropTypes.isRequired,
    eventOrganizationId: PropTypes.isRequired,
    price: PropTypes.isRequired,
    venueName: PropTypes.isRequired,
    eventOrganizationId: PropTypes.isRequired,
    bookedBy: PropTypes.isRequired,
    isAllowedToBook: PropTypes.isRequired,
    isSingleRegistrationPerTicket: PropTypes.isRequired,
    ticketPurchaseLimit: PropTypes.isRequired,
    ticketsCount: PropTypes.isRequired,
    seats: PropTypes.array,
    isSeat: PropTypes.bool.isRequired,
};